import { GetPriceError, GetPriceResult } from './types'
import { Token } from '@/domain/types/Token'
import { BaseUnitNumber, Percentage } from '@/domain/types/NumericValues'

export function getPriceImpactPercentage(
  priceData?: GetPriceResult | GetPriceError | null,
  opts: { mode: 'asset-swap' | 'debt-swap' } = { mode: 'asset-swap' },
): Percentage {
  if (!priceData || !('result' in priceData)) return Percentage(0)

  const inputUSD = Number(priceData.result.effectiveInputAmountUSD ?? priceData.result.inputAmountUSD ?? 0)
  const outputUSD = Number(priceData.result.effectiveOutputAmountUSD ?? priceData.result.outputAmountUSD ?? 0)

  if (!Number.isFinite(inputUSD) || inputUSD <= 0) return Percentage(0)

  const raw =
    opts.mode === 'debt-swap'
      ? (outputUSD - inputUSD) / inputUSD // more debt out than repaid in → bad
      : (inputUSD - outputUSD) / inputUSD // less value out than in → bad

  return Percentage(Math.max(0, raw), true)
}

export function estimateUSDDifference(priceData?: GetPriceResult | GetPriceError | null): string {
  if (!priceData || !('result' in priceData)) {
    return '0%'
  }

  if (!priceData.result.effectiveInputAmountUSD || !priceData.result.effectiveOutputAmountUSD) {
    return 'Could not fetch price data'
  }

  const inputAmountUSD = Number(priceData.result.effectiveInputAmountUSD)
  const outputAmountUSD = Number(priceData.result.effectiveOutputAmountUSD)
  const percentageDifference = ((outputAmountUSD - inputAmountUSD) / inputAmountUSD) * 100
  const formattedPercentageDifference = `${percentageDifference >= 0 ? '+' : ''}${percentageDifference.toFixed(2)}%`
  return formattedPercentageDifference
}

export function estimateTokenRatio({
  priceData,
  fromToken,
  toToken,
}: { priceData: GetPriceResult; fromToken: Token; toToken: Token }): string {
  const inputBaseAmount = BaseUnitNumber(priceData.result.inputAmount)
  const outputBaseAmount = BaseUnitNumber(priceData.result.outputAmount)

  const normalizedInputAmount = Number(fromToken.fromBaseUnit(inputBaseAmount))
  const normalizedOutputAmount = Number(toToken.fromBaseUnit(outputBaseAmount))

  const ratio = normalizedOutputAmount / normalizedInputAmount

  return ratio.toFixed(6)
}
